const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SEOService = {
  /**
   * Obtiene metadata SEO para un bote específico
   * @param {number} boatId - ID del bote
   * @returns {Promise<Object>} Metadata SEO del bote
   */
  async getBoatSEO(boatId) {
    try {
      const response = await fetch(`${API_BASE_URL}/seo/api/boat/${boatId}/`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching boat SEO:', error);
      throw error;
    }
  },

  /**
   * Obtiene metadata SEO para una página estática
   * @param {string} pageType - Tipo de página (home, about, services, faqs, terms, policy)
   * @returns {Promise<Object>} Metadata SEO de la página
   */
  async getStaticPageSEO(pageType) {
    try {
      const response = await fetch(`${API_BASE_URL}/seo/api/page/${pageType}/`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching static page SEO:', error);
      throw error;
    }
  },

  /**
   * Obtiene metadata SEO para la página del blog
   * @returns {Promise<Object>} Metadata SEO del blog
   */
  async getBlogSEO() {
    try {
      const response = await fetch(`${API_BASE_URL}/seo/api/blog/`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching blog SEO:', error);
      throw error;
    }
  },

  /**
   * Obtiene metadata SEO para un post específico del blog
   * @param {string} postSlug - Slug del post
   * @returns {Promise<Object>} Metadata SEO del post
   */
  async getBlogPostSEO(postSlug) {
    try {
      const response = await fetch(`${API_BASE_URL}/seo/api/blog/${postSlug}/`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching blog post SEO:', error);
      throw error;
    }
  },

  /**
   * Obtiene metadata SEO basado en la ruta actual
   * @param {string} pathname - Ruta actual
   * @param {Object} params - Parámetros de la ruta (boatId, postSlug, etc.)
   * @returns {Promise<Object>} Metadata SEO apropiada
   */
  async getSEOForPath(pathname, params = {}) {
    try {
      // Páginas estáticas
      if (pathname === '/' || pathname === '/home') {
        return await this.getStaticPageSEO('home');
      } else if (pathname === '/experience') {
        return await this.getStaticPageSEO('landing');
      } else if (pathname === '/about') {
        return await this.getStaticPageSEO('about');
      } else if (pathname === '/services') {
        return await this.getStaticPageSEO('services');
      } else if (pathname === '/faqs') {
        return await this.getStaticPageSEO('faqs');
      } else if (pathname === '/terms') {
        return await this.getStaticPageSEO('terms');
      } else if (pathname === '/policy') {
        return await this.getStaticPageSEO('policy');
      }
      
      // Páginas de botes
      else if (pathname.startsWith('/boats/') && params.boatId) {
        return await this.getBoatSEO(params.boatId);
      }
      
      // Página del blog
      else if (pathname === '/blog') {
        return await this.getBlogSEO();
      }
      
      // Posts del blog
      else if (pathname.startsWith('/blog/') && params.postSlug) {
        return await this.getBlogPostSEO(params.postSlug);
      }
      
      // Por defecto, usar home
      else {
        return await this.getStaticPageSEO('home');
      }
    } catch (error) {
      console.error('Error fetching SEO for path:', error);
      // Retornar metadata por defecto en caso de error
      return {
        title: 'Mr. Sea Miami - Boat Rentals',
        description: 'Premium boat rentals in Miami. Experience the best of Miami waters with our luxury boat fleet.',
        og_title: 'Mr. Sea Miami - Boat Rentals',
        og_description: 'Premium boat rentals in Miami. Experience the best of Miami waters with our luxury boat fleet.',
        og_image: '',
        og_type: 'website',
        twitter_card: 'summary_large_image',
        twitter_title: 'Mr. Sea Miami - Boat Rentals',
        twitter_description: 'Premium boat rentals in Miami. Experience the best of Miami waters with our luxury boat fleet.',
        twitter_image: '',
        canonical_url: window.location.href,
        og_url: window.location.href,
      };
    }
  }
};

export default SEOService;
